import React from 'react';
import { formatCell, humanizeHeader, normalizeColumnType, rowCountCaption } from './tableFormat';

// A typed table in a scroll region. Same prop contract as the other renderers
// ({ data, width, height, title }); `data` is the whole tabular payload (see
// renderers/index.js::rendererInput). `meta` is the live query metadata when there
// is one — a stored result has none, so the caption is built from the payload
// first and only refined by `meta`. `emptyMessage` renders a headers-only table for
// a result that matched nothing, so the reader still sees what the columns were.
//
// No virtualisation: the server caps rows at 100. The wrapper is the scroll
// region, focusable so keyboard users can scroll it, and it scrolls sideways
// itself rather than letting the page. The header stays put via sticky <th>s. The
// row count is said twice on purpose: the <caption> is what a screen reader
// announces with the table, and the visible line under the scroll region is what a
// sighted reader sees without scrolling to the last row.

function TableRenderer({ data, title, meta, emptyMessage }) {
  const columns = Array.isArray(data?.columns) ? data.columns : [];
  const types = Array.isArray(data?.column_types) ? data.column_types : [];
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const colTypes = columns.map((_, i) => normalizeColumnType(types[i]));
  // PRD-1578: absent on a result saved before the flags existed, which reads as "no
  // identifiers" -- separators as before -- until its next refresh supplies them.
  const idFlags = Array.isArray(data?.identifier_columns) ? data.identifier_columns : [];
  const caption = rows.length === 0 ? (emptyMessage || 'No matching records.') : rowCountCaption(rows.length, data?.total_rows, meta);
  // The result is larger than the page the card can hold. Distinct from "the PDF
  // clipped it": these are rows nobody can reach by scrolling.
  const capped = (Number(data?.total_rows) || 0) > rows.length;
  const label = title ? `${title} table` : 'Table';

  return (
    <div className="wc-table-shell">
      {/* A labelled region rather than a bare focusable div, so tabbing in is
          announced. */}
      <div className="wc-table-wrap" role="region" tabIndex={0} aria-label={label}>
        <table className="wc-table">
          <caption className="wc-table-caption wc-sr-only">{caption}</caption>
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th key={`${c}-${i}`} scope="col" className={`wc-th wc-th-${colTypes[i]}`} title={String(c)}>
                  {humanizeHeader(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="wc-td wc-td-empty" colSpan={Math.max(1, columns.length)}>
                  {emptyMessage || 'No matching records.'}
                </td>
              </tr>
            ) : (
              rows.map((row, r) => (
                <tr key={r}>
                  {columns.map((_, c) => {
                    const cell = formatCell(row?.[c], colTypes[c], undefined, { identifier: idFlags[c] === true });
                    return (
                      <td
                        key={c}
                        className={`wc-td wc-td-${cell.type}${cell.isNull ? ' wc-td-null' : ''}`}
                        title={cell.isNull ? undefined : String(row?.[c])}
                        aria-label={cell.isNull ? 'empty' : undefined}
                      >
                        {cell.text}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {rows.length > 0 && (
        <div className="wc-table-count" aria-hidden="true">{caption}</div>
      )}
      {/* Print only. The card keeps its on-screen height on paper and the rows
          scroll, so a PDF shows the rows that fit under a count describing the
          whole result. Say so rather than let the page imply it is complete.
          Expanding tables in print needs the grid to reflow (its items are
          absolutely positioned) and is tracked separately.

          Two different shortfalls, so two different sentences. A PDF always shows
          fewer rows than the card. On top of that the CARD itself is capped, so
          when the result is larger than the page the reader is short rows the
          screen never held either -- and the only way to get those is the CSV,
          which re-runs the query server-side rather than copying what is drawn. */}
      {rows.length > 0 && (
        <div className="wc-table-print-note">
          {capped
            ? `Showing the rows that fit. This table is capped at ${rows.length.toLocaleString()} of `
              + `${Number(data?.total_rows).toLocaleString()} matching rows — use Download CSV on the widget `
              + 'menu for the full result.'
            : 'Table scrolls on screen; this page shows the rows that fit.'}
        </div>
      )}
    </div>
  );
}

export default TableRenderer;
